import {
    Activity,
    AlertTriangle,
    Building2,
    CheckCircle,
    DollarSign,
    Smartphone,
    Store,
    TrendingUp,
    Users
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import EmptyStateGuide from '../../components/analytics/EmptyStateGuide';
import MetricDefinitionsDrawer from '../../components/analytics/MetricDefinitionsDrawer';
import MetricTooltip from '../../components/analytics/MetricTooltip';
import PageGuide from '../../components/analytics/PageGuide';
import { analyticsService } from '../../services/api';
import '../Analytics.css';
import AnalyticsLayout from './AnalyticsLayout';

// ─── helpers ─────────────────────────────────────────────────────────────────

function getDefaultFromDate() {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
}

function getDefaultToDate() {
    return new Date().toISOString().split('T')[0];
}

const fmtCur = v =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

const fmtNum = v => new Intl.NumberFormat('it-IT').format(v || 0);

/** Return the period immediately preceding [from, to] with the same duration */
function getPrevPeriod(from, to) {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const lenMs = toDate.getTime() - fromDate.getTime();
    const prevTo = new Date(fromDate.getTime() - 86400000);
    const prevFrom = new Date(prevTo.getTime() - lenMs);
    return {
        from: prevFrom.toISOString().split('T')[0],
        to: prevTo.toISOString().split('T')[0]
    };
}

function computeDelta(current, prev) {
    if (prev === null || prev === undefined || prev === 0) return null;
    const abs = current - prev;
    const pct = parseFloat(((abs / prev) * 100).toFixed(1));
    return { abs, pct, up: abs >= 0 };
}

function isPeriodWide(from, to) {
    if (!from || !to) return false;
    return (new Date(to) - new Date(from)) > 365 * 86400000;
}

const LOW_VOLUME_THRESHOLD = 10;

// ─── Delta badge ─────────────────────────────────────────────────────────────

function DeltaBadge({ current, prev }) {
    const d = computeDelta(current, prev);
    if (!d) return null;
    const sign = d.pct > 0 ? '+' : '';
    return (
        <span className={`kpi-delta kpi-delta--${d.up ? 'up' : 'down'}`}>
            {d.up ? '▲' : '▼'} {sign}{d.pct}%
        </span>
    );
}

// ─── Operational alerts builder ───────────────────────────────────────────────

function buildAlerts(data, opsData) {
    const alerts = [];

    const offlineCount = opsData?.devices?.offline ?? data?.devices?.inactive ?? 0;
    if (offlineCount > 0) {
        alerts.push({
            severity: offlineCount > 3 ? 'error' : 'warning',
            count: offlineCount,
            label: `device inattiv${offlineCount === 1 ? 'o' : 'i'}`,
            desc: 'Device non ACTIVE. Potrebbero non accettare pagamenti.',
            tab: '/admin/analytics/operations',
            linkLabel: '→ Operations'
        });
    }

    if (opsData?.workerIssues?.stripeOnboardingIncomplete > 0) {
        const n = opsData.workerIssues.stripeOnboardingIncomplete;
        alerts.push({
            severity: 'warning',
            count: n,
            label: `percettor${n === 1 ? 'e' : 'i'} con Stripe incompleto`,
            desc: 'Onboarding Stripe non completato: questi percettori non riceveranno i pagamenti.',
            tab: '/admin/analytics/activation',
            linkLabel: '→ Activation'
        });
    }

    if (opsData?.payments?.failed > 0) {
        const n = opsData.payments.failed;
        const rate = opsData.payments.failureRate || 0;
        alerts.push({
            severity: rate > 10 ? 'error' : 'warning',
            count: n,
            label: `pagament${n === 1 ? 'o' : 'i'} fallito/i`,
            desc: `${rate}% failure rate nel periodo.`,
            tab: '/admin/analytics/operations',
            linkLabel: '→ Operations'
        });
    }

    if (opsData?.invoiceRuns?.issueCount > 0) {
        const n = opsData.invoiceRuns.issueCount;
        alerts.push({
            severity: 'error',
            count: n,
            label: `invoice run con error${n === 1 ? 'e' : 'i'}`,
            desc: 'Alcune fatture non sono state emesse correttamente.',
            tab: '/admin/analytics/operations',
            linkLabel: '→ Operations'
        });
    }

    if (data?.tenants) {
        const inactive = (data.tenants.total || 0) - (data.tenants.active || 0);
        if (inactive > 0) {
            alerts.push({
                severity: 'info',
                count: inactive,
                label: `tenant non attiv${inactive === 1 ? 'o' : 'i'}`,
                desc: 'Tenant in stato non-ACTIVE (registrati, in onboarding o sospesi).',
                tab: '/admin/tenants',
                linkLabel: '→ Tenant'
            });
        }
    }

    return alerts;
}

// ─── component ───────────────────────────────────────────────────────────────

export default function AnalyticsOverview() {
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [prevData, setPrevData] = useState(null);
    const [revenueData, setRevenueData] = useState(null);
    const [opsData, setOpsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        from: getDefaultFromDate(),
        to: getDefaultToDate()
    });

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const prev = getPrevPeriod(filters.from, filters.to);

            const [overviewRes, prevOverviewRes, revenueRes, opsRes] =
                await Promise.allSettled([
                    analyticsService.getOverview({ from: filters.from, to: filters.to, groupBy: 'day' }),
                    analyticsService.getOverview({ from: prev.from, to: prev.to }),
                    analyticsService.getRevenue({ from: filters.from, to: filters.to }),
                    analyticsService.getOperations({ from: filters.from, to: filters.to })
                ]);

            if (overviewRes.status === 'rejected') {
                const err = overviewRes.reason;
                if (err.response?.status === 403)
                    throw new Error('La funzionalità Analytics non è abilitata. Contatta il supporto.');
                throw new Error(err.response?.data?.message || err.message || 'Errore nel caricamento dei dati');
            }

            setData(overviewRes.value);
            setPrevData(prevOverviewRes.status === 'fulfilled' ? prevOverviewRes.value : null);
            setRevenueData(revenueRes.status === 'fulfilled' ? revenueRes.value : null);
            setOpsData(opsRes.status === 'fulfilled' ? opsRes.value : null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { loadData(); }, [loadData]);

    const txCount = data?.tips?.transactionCount || 0;
    const eligibleAttempts = data?.tips?.eligibleAttempts ?? txCount;
    const lowVol = txCount > 0 && txCount < LOW_VOLUME_THRESHOLD;
    const wide = isPeriodWide(filters.from, filters.to);
    const alerts = buildAlerts(data, opsData);

    return (
        <AnalyticsLayout filters={filters} onFiltersChange={setFilters}>

            <PageGuide
                title="Overview"
                summary="Usa questa pagina per verificare rapidamente se Soontip sta crescendo, monetizzando e operando senza blocchi nel periodo selezionato. Per analisi di dettaglio usa Traction, Revenue, Activation e Operations."
            />

            {/* ── toolbar ──────────────────────────────────────────────────── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {data ? `Periodo: ${data.period?.from} → ${data.period?.to}` : ''}
                </span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <MetricDefinitionsDrawer
                        metricKeys={['totalTipVolume', 'successfulTransactions', 'estimatedSoontipFee', 'activeVenues', 'paymentSuccessRate', 'averageTipAmount', 'activeDevices', 'activeWorkers']}
                        label="Definizioni metriche"
                    />
                    <button
                        className="metric-drawer-trigger"
                        onClick={loadData}
                        disabled={loading}
                        style={{ marginBottom: 0 }}
                    >
                        {loading ? '⏳' : '🔄'} Aggiorna
                    </button>
                </div>
            </div>

            {/* ── loading ──────────────────────────────────────────────────── */}
            {loading && !data && (
                <div className="analytics-loading">
                    <div className="spinner"></div>
                    <p>Caricamento dati overview...</p>
                </div>
            )}

            {/* ── error ────────────────────────────────────────────────────── */}
            {error && (
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                    <strong>Errore:</strong> {error}
                </div>
            )}

            {/* ── main content ─────────────────────────────────────────────── */}
            {data && (
                <>
                    {/* Warning: periodo molto ampio */}
                    {wide && (
                        <div className="overview-wide-warning">
                            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                            <span>
                                Il periodo selezionato è molto ampio. Per analizzare l'andamento operativo usa{' '}
                                <strong>30d</strong>, <strong>90d</strong> o <strong>1y</strong>.
                            </span>
                        </div>
                    )}

                    {/* Sintesi del periodo */}
                    <div className="overview-summary">
                        <div className="overview-summary-title">Sintesi del periodo</div>
                        <p className="overview-summary-text">
                            {txCount > 0 ? (
                                <>
                                    Nel periodo selezionato Soontip ha processato{' '}
                                    <strong>{fmtNum(txCount)} transazion{txCount === 1 ? 'e' : 'i'} riuscit{txCount === 1 ? 'a' : 'e'}</strong>{' '}
                                    per <strong>{fmtCur(data.tips?.totalAmount)}</strong> di mance totali, con{' '}
                                    <strong>{data.venues?.active || 0} local{(data.venues?.active || 0) === 1 ? 'e attivo' : 'i attivi'}</strong>{' '}
                                    su {data.venues?.total || 0} e{' '}
                                    <strong>{opsData?.devices?.offline ?? data.devices?.inactive ?? 0} device inattiv{(opsData?.devices?.offline ?? data.devices?.inactive ?? 0) === 1 ? 'o' : 'i'}</strong>.
                                </>
                            ) : (
                                'Nessuna transazione registrata nel periodo selezionato.'
                            )}
                            {lowVol && (
                                <> <span className="overview-summary-lowvol">
                                    ⚠ Alcuni KPI sono basati su un volume transazionale basso.
                                </span></>
                            )}
                        </p>
                    </div>

                    {/* ── KPI principali ───────────────────────────────────────── */}
                    <div className="kpi-section">
                        <h2 className="section-title">KPI principali</h2>
                        <div className="kpi-grid">

                            {/* Mance Processate */}
                            <MetricTooltip metricKey="totalTipVolume">
                                <div
                                    className="kpi-card kpi-accent"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate('/admin/tip-transactions')}
                                >
                                    <div className="kpi-icon"><DollarSign size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Mance Processate</div>
                                        <div className="kpi-value">{fmtCur(data.tips?.totalAmount)}</div>
                                        <div className="kpi-meta" style={{ flexWrap: 'wrap', gap: '6px' }}>
                                            <TrendingUp size={14} />
                                            <span>{fmtNum(txCount)} transazioni</span>
                                            <DeltaBadge current={data.tips?.totalAmount} prev={prevData?.tips?.totalAmount} />
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            {/* Transazioni Riuscite */}
                            <MetricTooltip metricKey="successfulTransactions">
                                <div
                                    className="kpi-card kpi-success"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate('/admin/tip-transactions')}
                                >
                                    <div className="kpi-icon"><CheckCircle size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Transazioni Riuscite</div>
                                        <div className="kpi-value">{fmtNum(txCount)}</div>
                                        <div className="kpi-meta" style={{ flexWrap: 'wrap', gap: '6px' }}>
                                            <DeltaBadge current={txCount} prev={prevData?.tips?.transactionCount} />
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            {/* Revenue Soontip Stimata */}
                            <MetricTooltip metricKey="estimatedSoontipFee">
                                <div
                                    className="kpi-card kpi-primary"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate('/admin/analytics/revenue')}
                                >
                                    <div className="kpi-icon"><TrendingUp size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Revenue Soontip Stimata</div>
                                        <div className="kpi-value">
                                            {revenueData ? fmtCur(revenueData.estimatedSoontipFee) : '—'}
                                        </div>
                                        <div className="kpi-meta">
                                            <span style={{ fontSize: '0.73rem', color: '#f59e0b', fontStyle: 'italic' }}>
                                                ⚠ Stima — non è revenue fatturata
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            {/* Locali Attivi */}
                            <MetricTooltip metricKey="activeVenues">
                                <div
                                    className="kpi-card kpi-info"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate('/admin/analytics/traction')}
                                >
                                    <div className="kpi-icon"><Store size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Locali Attivi</div>
                                        <div className="kpi-value">{data.venues?.active || 0}</div>
                                        <div className="kpi-meta" style={{ flexWrap: 'wrap', gap: '6px' }}>
                                            <span>su {data.venues?.total || 0} totali</span>
                                            <DeltaBadge current={data.venues?.active} prev={prevData?.venues?.active} />
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>

                            {/* Tasso Successo Pagamenti */}
                            <MetricTooltip metricKey="paymentSuccessRate">
                                <div
                                    className="kpi-card kpi-success"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate('/admin/analytics/operations')}
                                >
                                    <div className="kpi-icon">
                                        <span style={{ fontSize: '26px', lineHeight: 1 }}>✓</span>
                                    </div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Tasso Successo Pagamenti</div>
                                        <div className="kpi-value">{data.tips?.successRate ?? 0}%</div>
                                        <div className="kpi-meta" style={{ flexWrap: 'wrap', gap: '6px' }}>
                                            <span className="kpi-sample">
                                                su {fmtNum(eligibleAttempts)} tentativ{eligibleAttempts === 1 ? 'o' : 'i'}
                                            </span>
                                        </div>
                                        {lowVol && (
                                            <div className="kpi-low-volume">⚠ Basso volume — dato poco significativo</div>
                                        )}
                                    </div>
                                </div>
                            </MetricTooltip>

                            {/* Mancia Media */}
                            <MetricTooltip metricKey="averageTipAmount">
                                <div
                                    className="kpi-card kpi-secondary"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate('/admin/tip-transactions')}
                                >
                                    <div className="kpi-icon"><Activity size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Mancia Media</div>
                                        <div className="kpi-value">{fmtCur(data.tips?.averageAmount)}</div>
                                        <div className="kpi-meta" style={{ flexWrap: 'wrap', gap: '6px' }}>
                                            <span className="kpi-sample">
                                                su {fmtNum(txCount)} transazion{txCount === 1 ? 'e' : 'i'}
                                            </span>
                                            <DeltaBadge current={data.tips?.averageAmount} prev={prevData?.tips?.averageAmount} />
                                        </div>
                                        {lowVol && (
                                            <div className="kpi-low-volume">⚠ Basso volume — dato poco significativo</div>
                                        )}
                                    </div>
                                </div>
                            </MetricTooltip>

                        </div>
                    </div>

                    {/* ── Infrastruttura ────────────────────────────────────── */}
                    <div className="kpi-section">
                        <h2 className="section-title section-title--secondary">Infrastruttura</h2>
                        <div className="kpi-grid-sm">

                            <div
                                className="kpi-card kpi-card-sm"
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate('/admin/tenants')}
                            >
                                <div className="kpi-icon-sm" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                    <Building2 size={20} color="#fff" />
                                </div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Tenant</div>
                                    <div className="kpi-value-sm">{data.tenants?.total || 0}</div>
                                    <div className="kpi-meta">
                                        <span style={{ color: '#10b981' }}>✓ {data.tenants?.active || 0} attivi</span>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="kpi-card kpi-card-sm"
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate('/admin/venues')}
                            >
                                <div className="kpi-icon-sm" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                                    <Store size={20} color="#fff" />
                                </div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Locali</div>
                                    <div className="kpi-value-sm">{data.venues?.total || 0}</div>
                                    <div className="kpi-meta">
                                        <span style={{ color: '#10b981' }}>✓ {data.venues?.active || 0} con transazioni</span>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="kpi-card kpi-card-sm"
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate('/admin/devices')}
                            >
                                <div className="kpi-icon-sm" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                                    <Smartphone size={20} color="#fff" />
                                </div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Device</div>
                                    <div className="kpi-value-sm">{data.devices?.total || 0}</div>
                                    <div className="kpi-meta">
                                        <span style={{ color: '#10b981' }}>✓ {data.devices?.active || 0} attivi</span>
                                        {(data.devices?.inactive || 0) > 0 && (
                                            <>
                                                <span style={{ margin: '0 4px', color: '#94a3b8' }}>·</span>
                                                <span style={{ color: '#f59e0b' }}>{data.devices?.inactive} inattivi</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div
                                className="kpi-card kpi-card-sm"
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate('/admin/analytics/activation')}
                            >
                                <div className="kpi-icon-sm" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
                                    <Users size={20} color="#fff" />
                                </div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Percettori</div>
                                    <div className="kpi-value-sm">{data.onboarding?.invited || 0}</div>
                                    <div className="kpi-meta">
                                        <span style={{ color: '#10b981' }}>✓ {data.onboarding?.completed || 0} accettati</span>
                                        {(data.onboarding?.pending || 0) > 0 && (
                                            <>
                                                <span style={{ margin: '0 4px', color: '#94a3b8' }}>·</span>
                                                <span style={{ color: '#f59e0b' }}>{data.onboarding?.pending} in attesa</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* ── Punti di attenzione ───────────────────────────────── */}
                    <div className="kpi-section">
                        <h2 className="section-title">Punti di attenzione</h2>
                        {alerts.length === 0 ? (
                            <div className="overview-alerts-clear">
                                <CheckCircle size={16} color="#10b981" />
                                <span>Nessuna anomalia rilevata nel periodo selezionato.</span>
                            </div>
                        ) : (
                            <div className="overview-alerts">
                                {alerts.map((alert, i) => (
                                    <div key={i} className={`overview-alert overview-alert--${alert.severity}`}>
                                        <span className="overview-alert-count">{alert.count}</span>
                                        <div className="overview-alert-body">
                                            <strong>{alert.label}</strong>
                                            <span className="overview-alert-desc"> — {alert.desc}</span>
                                        </div>
                                        <button
                                            className="overview-alert-link"
                                            onClick={() => navigate(alert.tab)}
                                        >
                                            {alert.linkLabel}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* ── Trend Mance ───────────────────────────────────────── */}
                    {data.tips?.trend && data.tips.trend.length === 0 && (
                        <EmptyStateGuide
                            condition={true}
                            icon="📈"
                            title="Nessun dato di trend disponibile"
                            message="Non ci sono transazioni nel periodo selezionato. Prova ad ampliare il range di date o a verificare che ci siano locali attivi con device funzionanti."
                        />
                    )}
                    {data.tips?.trend && data.tips.trend.length > 0 && (() => {
                        const trendData = data.tips.trend.map(item => ({
                            ...item,
                            label: new Date(item.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
                            media: item.count > 0 ? parseFloat((item.amount / item.count).toFixed(2)) : 0
                        }));
                        return (
                            <div className="trend-section">
                                <h2 className="section-title">Trend Mance</h2>

                                {/* Chart */}
                                <div style={{ width: '100%', height: 260, marginBottom: '24px' }}>
                                    <ResponsiveContainer>
                                        <AreaChart data={trendData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="gradImporto" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.25} />
                                                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="label"
                                                tick={{ fontSize: 11, fill: '#64748b' }}
                                                tickLine={false}
                                                axisLine={{ stroke: '#e2e8f0' }}
                                            />
                                            <YAxis
                                                tick={{ fontSize: 11, fill: '#64748b' }}
                                                tickLine={false}
                                                axisLine={false}
                                                tickFormatter={v => `€${v}`}
                                                width={52}
                                            />
                                            <Tooltip
                                                formatter={(value, name) => {
                                                    if (name === 'amount') return [fmtCur(value), 'Importo'];
                                                    if (name === 'media') return [fmtCur(value), 'Media'];
                                                    return [value, name];
                                                }}
                                                labelFormatter={label => `Data: ${label}`}
                                                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="amount"
                                                stroke="#6366f1"
                                                strokeWidth={2}
                                                fill="url(#gradImporto)"
                                                dot={{ r: 3, fill: '#6366f1' }}
                                                activeDot={{ r: 5 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Table */}
                                <div className="trend-table-container">
                                    <table className="trend-table">
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Importo</th>
                                                <th>Transazioni</th>
                                                <th>Media</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...trendData].reverse().map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>{new Date(item.date).toLocaleDateString('it-IT')}</td>
                                                    <td>{fmtCur(item.amount)}</td>
                                                    <td>{fmtNum(item.count)}</td>
                                                    <td>{fmtCur(item.media)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })()}
                </>
            )}
        </AnalyticsLayout>
    );
}
